
// src/components/Auth.tsx
import React, { useState } from 'react';
import { firebaseService } from '../../lib/firebase';
import { Button } from '@/components/ui/button';
import { User } from 'firebase/auth';

interface AuthProps {
  onLoginSuccess?: () => void;
  onLogout?: () => void;
}

const Auth: React.FC<AuthProps> = ({ onLoginSuccess, onLogout }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    // Check if user is already signed in
    const unsubscribe = firebaseService.getAuth().onAuthStateChanged((user) => {
      setUser(user);
      if (user && onLoginSuccess) {
        onLoginSuccess();
      }
    });

    return () => unsubscribe();
  }, [onLoginSuccess]);

  const handleSignIn = async () => {
    setLoading(true);
    try {
      const user = await firebaseService.signInWithGoogle();
      setUser(user);
      if (user && onLoginSuccess) {
        onLoginSuccess();
      }
    } catch (error) {
      console.error('Error signing in:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    setLoading(true);
    try {
      await firebaseService.signOut();
      setUser(null);
      if (onLogout) {
        onLogout();
      }
    } catch (error) {
      console.error('Error signing out:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {user ? (
        <div className="flex items-center gap-2">
          {user.photoURL && (
            <img
              src={user.photoURL}
              alt={user.displayName || 'User'}
              className="h-8 w-8 rounded-full"
            />
          )}
          <div className="flex flex-col">
            <span className="text-sm font-medium">{user.displayName}</span>
            <span className="text-xs text-muted-foreground">{user.email}</span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleSignOut}
            disabled={loading}
          >
            {loading ? 'Signing out...' : 'Sign out'}
          </Button>
        </div>
      ) : (
        <Button
          variant="outline"
          onClick={handleSignIn}
          disabled={loading}
          className="w-full"
        >
          {loading ? 'Signing in...' : 'Sign in with Google'}
        </Button>
      )}
    </div>
  );
};

export default Auth;
